import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { db, auth } from "./firebase";
import { collection, addDoc, doc, updateDoc, deleteDoc, query, orderBy, onSnapshot, serverTimestamp, Timestamp } from "firebase/firestore";

const GroupChatPage = () => {
    const { groupId } = useParams();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editedMessage, setEditedMessage] = useState("");
    const [customMessage, setCustomMessage] = useState("");
    const [customTimestamp, setCustomTimestamp] = useState("");

    // Ref to track chat container for auto-scroll
    const chatEndRef = useRef(null);

    useEffect(() => {
        const messagesRef = collection(db, "groups", groupId, "messages");
        const q = query(messagesRef, orderBy("timestamp", "desc")); // Get latest messages first

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedMessages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMessages(fetchedMessages.reverse()); // Reverse to maintain proper order
        });

        return () => unsubscribe();
    }, [groupId]);

    // Scroll to bottom when messages update
    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    // Send normal message
    const sendMessage = async () => {
        if (newMessage.trim() === "") return;
        await addDoc(collection(db, "groups", groupId, "messages"), {
            sender: auth.currentUser.uid,
            message: newMessage.trim(),
            timestamp: serverTimestamp(),
        });
        setNewMessage("");
    };

    // Send message with a custom timestamp
    const sendCustomMessage = async () => {
        if (customMessage.trim() === "" || customTimestamp.trim() === "") return;
        await addDoc(collection(db, "groups", groupId, "messages"), {
            sender: auth.currentUser.uid,
            message: customMessage.trim(),
            timestamp: Timestamp.fromDate(new Date(customTimestamp)),
        });
        setCustomMessage("");
        setCustomTimestamp("");
    };

    // Enable editing mode
    const startEditing = (msg) => {
        setEditingMessageId(msg.id);
        setEditedMessage(msg.message);
    };

    // Save edited message
    const saveEdit = async (msgId) => {
        if (editedMessage.trim() === "") return;
        await updateDoc(doc(db, "groups", groupId, "messages", msgId), {
            message: editedMessage.trim(),
        });
        setEditingMessageId(null);
    };

    // Delete message
    const deleteMessage = async (msgId) => {
        await deleteDoc(doc(db, "groups", groupId, "messages", msgId));
    };

    return (
        <div className="p-4">
            <h2 className="text-2xl font-bold mb-4">Group Chat: {groupId}</h2>

            {/* Messages List */}
            <div className="border p-4 rounded h-96 overflow-y-auto flex flex-col">
                {messages.map((msg) => (
                    <div key={msg.id} className="border-b p-2 flex justify-between">
                        <div>
                            <p><strong>{"Admin "}</strong>: {msg.message}</p>
                            <p className="text-xs text-gray-500">{msg.timestamp?.toDate().toLocaleString()}</p>
                        </div>
                        <div>
                            {auth.currentUser.uid === msg.sender && (
                                <>
                                    {editingMessageId === msg.id ? (
                                        <>
                                            <input
                                                type="text"
                                                value={editedMessage}
                                                onChange={(e) => setEditedMessage(e.target.value)}
                                                className="border px-2 py-1 w-40"
                                            />
                                            <button onClick={() => saveEdit(msg.id)} className="ml-2 text-green-500">Save</button>
                                        </>
                                    ) : (
                                        <>
                                            <button onClick={() => startEditing(msg)} className="text-blue-500 mr-2">Edit</button>
                                            <button onClick={() => deleteMessage(msg.id)} className="text-red-500">Delete</button>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                ))}
                {/* Empty div to ensure auto-scroll to the latest message */}
                <div ref={chatEndRef}></div>
            </div>

            {/* Normal Message Input */}
            <div className="mt-4">
                <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="border px-2 py-1 w-full"
                    placeholder="Type a message..."
                />
                <button onClick={sendMessage} className="bg-blue-500 text-white px-4 py-1 mt-2">Send</button>
            </div>

            {/* Custom Timestamp Message */}
            <div className="mt-4">
                <input
                    type="text"
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    className="border px-2 py-1 w-full"
                    placeholder="Custom message..."
                />
                <input
                    type="datetime-local"
                    value={customTimestamp}
                    onChange={(e) => setCustomTimestamp(e.target.value)}
                    className="border px-2 py-1 w-full mt-2"
                />
                <button onClick={sendCustomMessage} className="bg-purple-500 text-white px-4 py-1 mt-2">Send with Timestamp</button>
            </div>
        </div>
    );
};

export default GroupChatPage;